'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    FinancialSettingsBranch,
    FinancialSettingsScope,
    FinancialTabPayload,
    applyFinancialSettingsToAll,
    getFinancialSettingsBranches,
    getFinancialSettingsTab,
} from '@/lib/api/financial/financial-settings-api'
import { useEffect, useState } from 'react'
import { MdContentCopy, MdDownload } from 'react-icons/md'
import { toast } from 'react-toastify'

interface TabBranchActionsProps {
    /** Qual aba está em tela — é a unidade que se importa e se replica. */
    scope: FinancialSettingsScope
    /** Nome da aba como o operador a vê, para as mensagens. */
    label: string
    /**
     * O que está NA TELA agora, no formato que o backend grava.
     *
     * "Aplicar em todas" envia isto, não o que está salvo: o operador acabou de
     * ajustar os campos e espera que seja esse ajuste a se espalhar — inclusive
     * na filial atual, que entra na replicação como qualquer outra.
     */
    buildPayload: () => FinancialTabPayload
    /** Recebe a configuração lida de outra filial, para preencher os campos. */
    onImported: (payload: FinancialTabPayload) => void
    disabled?: boolean
}

/**
 * Importar de outra filial e aplicar em todas, para uma aba.
 *
 * Sem isto, uma rede com 15 filiais repetia a mesma configuração 15 vezes — e
 * bastava esquecer uma para o cliente ser cobrado com regra diferente conforme a
 * unidade onde marcou.
 *
 * As duas ações NÃO salvam sozinhas a filial atual no caso do importar: o
 * conteúdo cai nos campos e o operador confere antes de salvar, como faria
 * digitando. "Aplicar em todas", ao contrário, grava — inclusive aqui.
 */
export default function TabBranchActions({
    scope,
    label,
    buildPayload,
    onImported,
    disabled = false,
}: TabBranchActionsProps) {
    const [branches, setBranches] = useState<FinancialSettingsBranch[]>([])
    const [loadingBranches, setLoadingBranches] = useState(true)
    const [showImport, setShowImport] = useState(false)
    const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('')
    const [importing, setImporting] = useState(false)
    const [showApplyAll, setShowApplyAll] = useState(false)
    const [applying, setApplying] = useState(false)

    useEffect(() => {
        let alive = true
        getFinancialSettingsBranches()
            .then(res => {
                if (!alive) return
                if (res.success) setBranches(res.result || [])
            })
            .finally(() => {
                if (alive) setLoadingBranches(false)
            })
        return () => {
            alive = false
        }
    }, [])

    // Filial sozinha na organização: não há de onde importar nem para onde
    // aplicar, e dois botões inertes só confundem.
    if (loadingBranches || branches.length === 0) return null

    const handleImport = async () => {
        if (!selectedBranchId) return
        setImporting(true)
        try {
            const res = await getFinancialSettingsTab(scope, selectedBranchId)
            if (res.success && res.result) {
                onImported(res.result)
                const branch = branches.find(b => b.id === selectedBranchId)
                toast.success(
                    `Configuração de ${label} importada de ${
                        branch?.tradingName || branch?.name || 'outra filial'
                    }. Confira e salve para aplicar nesta filial.`
                )
                setShowImport(false)
                setSelectedBranchId('')
            } else {
                toast.error(res.error || `Erro ao importar ${label}`)
            }
        } catch {
            toast.error(`Erro ao importar ${label}`)
        } finally {
            setImporting(false)
        }
    }

    const handleApplyAll = async () => {
        setApplying(true)
        try {
            const res = await applyFinancialSettingsToAll(scope, buildPayload())
            if (res.success && res.result) {
                toast.success(
                    `${label} aplicada em ${res.result.applied} ${
                        res.result.applied === 1 ? 'filial' : 'filiais'
                    }.`
                )
                setShowApplyAll(false)
            } else {
                toast.error(res.error || `Erro ao aplicar ${label} nas filiais`)
            }
        } catch {
            toast.error(`Erro ao aplicar ${label} nas filiais`)
        } finally {
            setApplying(false)
        }
    }

    return (
        <>
            <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                    type="button"
                    className="btn btn-outline btn-sm gap-2"
                    onClick={() => setShowImport(true)}
                    disabled={disabled}
                    title={`Copiar a configuração de ${label} de outra filial para esta tela`}
                >
                    <MdDownload className="text-base" />
                    Importar de outra filial
                </button>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm gap-2"
                    onClick={() => setShowApplyAll(true)}
                    disabled={disabled}
                    title={`Gravar esta configuração de ${label} em todas as filiais`}
                >
                    <MdContentCopy className="text-base" />
                    Aplicar em todas as filiais
                </button>
            </div>

            {/* Importar: escolhe a filial de origem. */}
            {showImport && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box max-w-md">
                        <h3 className="text-lg font-bold">
                            Importar {label} de outra filial
                        </h3>
                        <p className="mt-2 text-sm text-base-content/70">
                            A configuração da filial escolhida vem para esta tela.
                            Nada é gravado até você salvar.
                        </p>

                        <div className="form-control mt-6 w-full">
                            <label
                                className="label pb-2"
                                htmlFor="import-branch"
                            >
                                <span className="label-text font-medium">
                                    Filial de origem
                                </span>
                            </label>
                            <select
                                id="import-branch"
                                className="select select-bordered w-full"
                                value={selectedBranchId}
                                onChange={e =>
                                    setSelectedBranchId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : ''
                                    )
                                }
                            >
                                <option value="">Selecione uma filial</option>
                                {branches.map(branch => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.tradingName || branch.name}
                                    </option>
                                ))}
                            </select>
                            <label className="label pt-2">
                                <span className="label-text-alt text-base-content/50">
                                    Somente filiais da mesma organização.
                                </span>
                            </label>
                        </div>

                        <div className="modal-action mt-6">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setShowImport(false)}
                                disabled={importing}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary gap-2"
                                onClick={handleImport}
                                disabled={!selectedBranchId || importing}
                            >
                                {importing && (
                                    <span className="loading loading-spinner loading-xs" />
                                )}
                                Importar
                            </button>
                        </div>
                    </div>
                    <form method="dialog" className="modal-backdrop">
                        <button
                            type="button"
                            onClick={() => setShowImport(false)}
                        >
                            fechar
                        </button>
                    </form>
                </dialog>
            )}

            <ConfirmModal
                isOpen={showApplyAll}
                onClose={() => setShowApplyAll(false)}
                onConfirm={handleApplyAll}
                loading={applying}
                title={`Aplicar ${label} em todas as filiais`}
                message={
                    `A configuração de ${label} que está nesta tela substitui a das ` +
                    `${branches.length + 1} filiais da organização, inclusive esta. ` +
                    `A configuração atual de cada uma é perdida.`
                }
                confirmButtonText="Aplicar em todas"
                confirmButtonClass="btn btn-secondary !border-0 !rounded-lg"
            />
        </>
    )
}
